import { useContext, useState } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import styles from './styles.module.css'
import PrimaryButton from '../../../../../global/components/buttons/primary-button/PrimaryButton'
import CircularProgress from '@mui/material/CircularProgress'
import { ToastNotificationContext } from '../../../../../global/context/toast-context/ToastNotificationContext'
import { fetchData } from '../../../../../global/utils/fetch'
import { putTireDeliveryData } from '../api'
import Table from '../../../../../global/components/table/Table'
import { Column } from 'react-table'
import TableTopper from '../../../../../global/components/table/helpers/table-topper/TableTopper'
import TableTitle from '../../../../../global/components/table/helpers/table-title/TableTitle'
import isDayOptimized from '../../../../../global/utils/is-day-optimized'
import { useNavigate } from 'react-router-dom'

interface IEligibleDelivery {
  tireRackOrderNumber: string
  deliverCandidateObjectId: string
}

interface IIneligibleDelivery {
  tireRackOrderNumber: string
  error: string
}

interface IUploadTireDeliveryModalProps {
  isOpen: boolean
  handleClosed: () => void
}

function UploadTireDeliveryModal(props: IUploadTireDeliveryModalProps) {
  const { isOpen, handleClosed } = props
  const [isFileUploading, setIsFileUploading] = useState(false)
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const navigate = useNavigate()
  const [eligibleDeliveries, setEligibleDeliveries] = useState<
    IEligibleDelivery[]
  >([])
  const [ineligibleDeliveries, setInelgibleDeliveries] = useState<
    IIneligibleDelivery[]
  >([])

  const title = 'Upload Tire Rack Delivery Data'
  const description =
    'Please upload the Tire Rack CSV for orders marked for delivery here.'

  function handleGoToTireRackDeliveriesQueue() {
    navigate('/queues/tire-rack-deliveries')
  }

  function handleUploadButtonClick(e: any) {
    const today = new Date()
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    const tomorrowTimestamp = tomorrow.getTime()
    const isTodaySaturday = today.getDay() === 6

    if (isTodaySaturday) {
      alert('Any deliveries uploaded today will be routed for Monday.')
    } else if (isDayOptimized(tomorrowTimestamp) && !isTodaySaturday) {
      alert(
        [
          "WARNING: Tomorrow's deliveries have already been routed and scheduled.",
          'Continuing to upload will queue all new deliveries for the next available day.',
          'They will not be scheduled for tomorrow.',
        ].join(' '),
      )
    }

    const fileInput = document.getElementById('fileUpload') as HTMLInputElement
    fileInput?.click()
  }

  async function handleUploadFileInput(e: any) {
    setIsFileUploading(true)
    try {
      const formData = new FormData()
      const file = e.target.files[0]
      formData.append('file', file)
      const tireDeliveryReponse = await fetchData<{
        eligibleDeliveries: IEligibleDelivery[]
        ineligibleDeliveries: IIneligibleDelivery[]
      }>(putTireDeliveryData(formData))

      const { eligibleDeliveries, ineligibleDeliveries } = tireDeliveryReponse

      setEligibleDeliveries(eligibleDeliveries)
      setInelgibleDeliveries(ineligibleDeliveries)

      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'File uploaded successfully!',
        severity: 'success',
      })
    } catch (error: any) {
      console.error(error)

      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: error.message ?? '',
        severity: 'error',
      })
    } finally {
      e.target.value = null
      setIsFileUploading(false)
    }
  }

  function prepareResultsData() {
    const eligible = eligibleDeliveries.map((item) => ({
      tireRackOrderNumber: item.tireRackOrderNumber,
      isSuccessful: true,
      orderObjectId: item.deliverCandidateObjectId,
      error: null,
    }))

    const ineligible = ineligibleDeliveries.map((item) => ({
      tireRackOrderNumber: item.tireRackOrderNumber,
      isSuccessful: false,
      orderObjectId: null,
      error: item.error,
    }))

    return [...eligible, ...ineligible]
  }

  // Combined columns for all tire rack orders
  const combinedOrdersColumns: Column<any>[] = [
    {
      Header: 'TR Order Number',
      accessor: 'tireRackOrderNumber',
    },
    {
      Header: 'Status',
      Cell: ({ row }: any) => (
        <span
          style={{
            color: row.original.isSuccessful ? 'green' : 'red',
          }}
        >
          {row.original.isSuccessful ? 'Success' : 'Failed'}
        </span>
      ),
    },
    {
      Header: 'Error',
      Cell: ({ row }: any) =>
        row.original.isSuccessful ? <></> : <span>{row.original.error}</span>,
    },
  ]

  return (
    <div>
      <Modal
        open={isOpen}
        onClose={() => {
          // we will prompt the user to confirm if they merely click out of the modal as opposed to clicking the close button
          if (!window.confirm('You are leaving the upload page. Continue?')) {
            return
          }
          handleClosed()
        }}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box className={styles.uploadModalContainer}>
          <div className={styles.title}>{title}</div>
          <div className={styles.contentContainer}>
            <div>{description}</div>
            <div
              onClick={handleGoToTireRackDeliveriesQueue}
              style={{ whiteSpace: 'nowrap' }}
              className={styles.uploadButton}
            >
              Go to Tire Rack Deliveries Queue
            </div>
            <input
              style={{ display: 'none' }}
              id='fileUpload'
              onChange={handleUploadFileInput}
              type='file'
              accept='.csv'
            />
            <div
              onClick={handleUploadButtonClick}
              style={{ whiteSpace: 'nowrap' }}
              className={styles.uploadButton}
            >
              Upload File
            </div>
          </div>
          {isFileUploading && <CircularProgress size={20} />}
          {(eligibleDeliveries.length > 0 ||
            ineligibleDeliveries.length > 0) && (
            <div style={{ width: '100%', overflowY: 'scroll' }}>
              <TableTopper>
                <div
                  style={{
                    display: 'flex',
                    gap: '1rem',
                    flexDirection: 'column',
                  }}
                >
                  <TableTitle>
                    <>
                      Upload Results ({eligibleDeliveries.length}/
                      {eligibleDeliveries.length + ineligibleDeliveries.length})
                    </>
                  </TableTitle>
                </div>
              </TableTopper>
              <Table
                columns={combinedOrdersColumns}
                data={prepareResultsData()}
                isDisplayMode={true}
              />
            </div>
          )}
          <PrimaryButton
            onClick={handleClosed}
            buttonName={'Close'}
            style={{ marginTop: '20px' }}
            disabled={isFileUploading}
          />
        </Box>
      </Modal>
    </div>
  )
}

export default UploadTireDeliveryModal
